import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const ThemeContext = createContext({});

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

const STORAGE_KEY = 'onthego_theme';

const getSystemPreference = () => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)')?.matches ? 'dark' : 'light';
  }
  return 'light';
};

const getInitialTheme = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'dark' || stored === 'light') return stored;
    if (stored === 'system') return getSystemPreference();
  } catch (_) {}
  return getSystemPreference();
};

export const ThemeProvider = ({ children }) => {
  // 'light' | 'dark' | 'system'
  const [preference, setPreference] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'dark' || stored === 'light' || stored === 'system') return stored;
    } catch (_) {}
    return 'system';
  });

  const [resolvedTheme, setResolvedTheme] = useState(() => getInitialTheme());

  // Apply theme to <html> element
  const applyTheme = useCallback((theme) => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList?.add('dark');
    } else {
      root.classList?.remove('dark');
    }
    setResolvedTheme(theme);
  }, []);

  // Resolve preference → actual theme
  const resolvePreference = useCallback((pref) => {
    if (pref === 'system') return getSystemPreference();
    return pref;
  }, []);

  // On preference change: persist to localStorage, apply to DOM, save to Supabase
  const setTheme = useCallback(async (newPreference) => {
    setPreference(newPreference);
    try {
      localStorage.setItem(STORAGE_KEY, newPreference);
    } catch (_) {}

    const resolved = resolvePreference(newPreference);
    applyTheme(resolved);

    // Persist to Supabase user metadata (fire-and-forget)
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (user) {
        await supabase?.auth?.updateUser({ data: { themePreference: newPreference } });
        // Also update user_profiles table if it has a theme column
        await supabase?.from('user_profiles')
          ?.update({ theme_preference: newPreference })
          ?.eq('id', user?.id);
      }
    } catch (_) {
      // Silently fail — localStorage is the source of truth
    }
  }, [applyTheme, resolvePreference]);

  // On mount: apply stored theme and listen for system changes
  useEffect(() => {
    const resolved = resolvePreference(preference);
    applyTheme(resolved);

    // Listen for system preference changes
    const mediaQuery = window.matchMedia?.('(prefers-color-scheme: dark)');
    const handleSystemChange = (e) => {
      if (preference === 'system') {
        applyTheme(e?.matches ? 'dark' : 'light');
      }
    };

    mediaQuery?.addEventListener?.('change', handleSystemChange);
    return () => mediaQuery?.removeEventListener?.('change', handleSystemChange);
  }, [preference, applyTheme, resolvePreference]);

  // On mount: try to load theme from Supabase user metadata
  useEffect(() => {
    const loadFromSupabase = async () => {
      try {
        const { data: { user } } = await supabase?.auth?.getUser();
        if (user?.user_metadata?.themePreference) {
          const remotePreference = user?.user_metadata?.themePreference;
          // Only apply if no local override exists
          const local = localStorage.getItem(STORAGE_KEY);
          if (!local) {
            setPreference(remotePreference);
            try { localStorage.setItem(STORAGE_KEY, remotePreference); } catch (_) {}
            applyTheme(resolvePreference(remotePreference));
          }
        }
      } catch (_) {}
    };
    loadFromSupabase();
  }, [applyTheme, resolvePreference]);

  const isDark = resolvedTheme === 'dark';

  return (
    <ThemeContext.Provider value={{ theme: resolvedTheme, preference, setTheme, isDark }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
